import React from 'react';
import { Link } from 'react-router-dom';
import { useTutorials } from '../../hooks/useTutorials';
import { useTutorialCategories } from '../../hooks/useTutorialCategories';
import { useTutorialCompletion } from '../../hooks/useTutorialCompletion';
import { useAuth } from '../../hooks/useAuth';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import Breadcrumb from '../../components/Breadcrumb';
import TutorialCard from '../../components/tutorials/TutorialCard';
import { generateTutorialsHomeBreadcrumb } from '../../utils/breadcrumb';
import { Loader2, BookOpen, ArrowRight, Folder, Award } from 'lucide-react';

export default function TutorialsHome() {
  const { tutorials, loading, error } = useTutorials();
  const { categories, loading: categoriesLoading, error: categoriesError } = useTutorialCategories();
  const { user } = useAuth();
  const { getCompletionStatus, completions } = useTutorialCompletion();
  const { setItems } = useBreadcrumb();

  const completedCount = completions.length;
  const totalCount = tutorials.length;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Format category slug for display
  const formatCategoryName = (slug: string) => {
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  React.useEffect(() => {
    setItems(generateTutorialsHomeBreadcrumb());
  }, [setItems]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb />
        
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4">Tutorials</h1>
          <p className="text-gray-600 text-lg mb-6">
            Step-by-step guides to help you succeed with your garden.
            Each tutorial includes written instructions and video walkthroughs.
          </p>

          {user && totalCount > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-primary-500">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-primary-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Your Progress</h2>
                </div>
                <div className="text-sm font-medium text-gray-700">
                  {completedCount} of {totalCount} completed
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-primary-500 h-full rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {progressPercentage}% complete
              </p>
            </div>
          )}
        </div>

        {/* Tutorial Categories */}
        {!categoriesError && categories.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Folder className="h-5 w-5 text-primary-500" />
              <h2 className="text-xl font-semibold">Browse by Category</h2>
            </div>
            {categoriesLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category) => (
                  <Link
                    key={category.category_slug}
                    to={category.category_slug === 'fall-greens-garden' 
                      ? '/tutorials/fall-greens-grow-box' 
                      : `/tutorials/category/${category.category_slug}`}
                    className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 hover:border-primary-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary-100 rounded-lg">
                        <BookOpen className="h-5 w-5 text-primary-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {formatCategoryName(category.category_slug)}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {category.count} tutorial{category.count !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-primary-500 transition-colors" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* All Tutorials */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">All Tutorials</h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          </div>
        ) : error ? (
          <div className="text-red-600 bg-red-50 p-4 rounded-lg">
            Error loading tutorials: {error}
          </div>
        ) : tutorials.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">No tutorials available yet.</p>
            <p className="text-gray-500">Check back soon for new content!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tutorials.map((tutorial) => (
              <TutorialCard
                key={tutorial.id}
                tutorial={tutorial}
                isCompleted={user ? getCompletionStatus(tutorial.id) : false}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}